import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { fetchWorkflowArtifacts } from '@/services/logicAppsApi';

/**
 * Detects if a workflow is a conversational agent based on its kind field.
 * The kind field is at the top level of workflow.json (not inside definition).
 *
 * A workflow is considered conversational if kind is 'Agent' (case-insensitive).
 * Conversational agents support real-time interactive chat.
 */
export const isAgentWorkflowKind = (kind?: string): boolean => {
  if (!kind) return false;
  return kind.toLowerCase() === 'agent';
};

/**
 * Detects if a workflow has any Agent actions (type: "Agent").
 * This indicates the workflow is an autonomous agent workflow.
 */
export const hasAgentActions = (definition?: {
  actions?: Record<string, { type?: string }>;
}): boolean => {
  if (!definition?.actions) return false;

  // Check if any action has type === "Agent"
  return Object.values(definition.actions).some(
    action => action.type === 'Agent'
  );
};

interface UseIsAgenticWorkflowOptions {
  workflowName: string | undefined;
  isDraft?: boolean | undefined;
  enabled?: boolean | undefined;
}

interface UseIsAgenticWorkflowReturn {
  /** Whether the workflow has Agent actions (autonomous agent) */
  hasAgentAction: boolean;
  /** Whether the workflow has kind: "Agent" (conversational agent with live chat) */
  isConversational: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Hook to determine if the current workflow is an agentic workflow.
 * Fetches the workflow definition and checks for agent patterns.
 */
export const useIsAgenticWorkflow = (
  options: UseIsAgenticWorkflowOptions
): UseIsAgenticWorkflowReturn => {
  const { workflowName, enabled = true } = options;
  const { getOttoToken, isAuthenticated } = useAuth();
  const { selectedApp, selectedProject } = useAppStore();


  const canFetch = !!(
    enabled &&
    isAuthenticated &&
    selectedProject &&
    selectedApp &&
    workflowName
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ['workflowDefinition', selectedProject?.toLowerCase(), selectedApp?.name?.toLowerCase(), workflowName],
    queryFn: async () => {
      if (!selectedProject || !selectedApp || !workflowName) {
        return null;
      }

      return fetchWorkflowArtifacts({
        workflowName,
        projectName: selectedProject ?? '',
        appName: selectedApp?.name ?? '',
        getToken: getOttoToken,
      });
    },
    enabled: canFetch,
    staleTime: 5 * 60 * 1000, // 5 minutes - workflow definition doesn't change often
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  const workflowFile = useMemo(() => options.isDraft ? 'workflow-draft.json' : 'workflow.json', [options.isDraft]);
  // Conversational agent: workflow has kind: "Agent" at top level
  // These support real-time interactive chat
  const isConversational = useMemo(() => {
    if (!data?.files) return false;

    const workflowJson = data.files[workflowFile];
    if (!workflowJson) return false;

    // The kind field is at the top level of workflow.json
    // Example: { "definition": {...}, "kind": "Agent" }
    return isAgentWorkflowKind(workflowJson.kind);
  }, [data, workflowFile]);

  // Autonomous agent: workflow has Agent actions but NOT kind: "Agent"
  // These show read-only chat log (no interactive chat)
  const hasAgentAction = useMemo(() => {
    if (!data?.files) return false;

    const workflowJson = data.files[workflowFile];
    if (!workflowJson?.definition) return false;

    // If it's conversational, don't count it as autonomous
    if (isAgentWorkflowKind(workflowJson.kind)) return false;

    // Check if any action has type === "Agent"
    return hasAgentActions(workflowJson.definition);
  }, [data, workflowFile]);

  return {
    hasAgentAction,
    isConversational,
    isLoading,
    isError,
  };
};
